// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

// Types and API
import { linkExamToDevice } from "../api";
import { ExamType } from "src/services/exams/types";

export const useLinkExamDevice = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();
  const { devices, setDevices } = useDevicesContext();

  return useMutation({
    mutationFn: async (payload: { deviceId: string; exam: ExamType }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return linkExamToDevice(token, clinicId, payload.deviceId, {
          examId: payload.exam.id,
        });
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      // Patch the device locally instead of waiting on the refetch that
      // invalidateQueries triggers, so the UI updates in one round-trip instead of two.
      setDevices(
        (devices ?? []).map((device) =>
          device.id === variables.deviceId
            ? {
                ...device,
                associatedExam: {
                  id: "",
                  assignmentDate: new Date().toISOString(),
                  unassignmentDate: null,
                  device,
                  exam: variables.exam,
                },
              }
            : device
        )
      );

      // Invalidate query caches so the background refetch keeps everything
      // (including other clients) eventually consistent with the server.
      queryClient.invalidateQueries({ queryKey: ["exams"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-exams"] });
      queryClient.invalidateQueries({ queryKey: ["linked-exams"] });
      queryClient.invalidateQueries({ queryKey: ["clinic-devices", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("Device linked successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Device could not be linked!"),
      });
    },
  });
};
